import { Type, Static } from '@sinclair/typebox';
import { Subcription } from './namespace';
import {
  PLAN_ID_BY_ENUM,
  ADDONS,
  PlanId,
  PlanSize,
  QuantityAddonCode,
  getIncludedTeamSeats,
  getIncludedGmb,
  getIncludedProEmailPerUser,
} from './subscription';

// =============================================================================
// Source de vérité partagée backend + frontend pour les limites d'abonnement
// quantitatives (compteurs de ressources gated par le plan + add-ons).
// Pour les capabilities qualitatives, voir CAPABILITIES dans subscription.ts.
// =============================================================================

export enum LimitResource {
  TEAM_MEMBER = 'TEAM_MEMBER',
  GMB = 'GMB',
  MAILBOX = 'MAILBOX',
}

export interface LimitDefinition {
  resource: LimitResource;
  /** Quantité incluse pour un plan/size donné, avant addons. */
  getIncluded(plan: Subcription, size: PlanSize): number;
  /** Addon quantitatif qui augmente la limite (1 unité = +1 ressource). */
  addon_code?: QuantityAddonCode;
  /** Plan minimum suggéré pour upgrader si l'addon n'est pas éligible sur le plan courant. */
  upgrade_target_plan?: PlanId;
  /** Clé d'erreur backend correspondante (cf. API_ERRORS côté frontend). */
  error_key: string;
}

export const LIMITS_REGISTRY: Record<LimitResource, LimitDefinition> = {
  [LimitResource.TEAM_MEMBER]: {
    resource: LimitResource.TEAM_MEMBER,
    getIncluded: (plan: Subcription, size: PlanSize): number => getIncludedTeamSeats(plan, size === 'team'),
    addon_code: 'extra_user',
    upgrade_target_plan: 'max',
    error_key: 'TEAM_MEMBER_LIMIT_REACHED',
  },
  [LimitResource.GMB]: {
    resource: LimitResource.GMB,
    getIncluded: (plan: Subcription): number => getIncludedGmb(plan),
    addon_code: 'extra_gmb',
    upgrade_target_plan: 'max',
    error_key: 'GMB_LIMIT_REACHED',
  },
  // Boîtes email hébergées (Stalwart). Quota = boîtes incluses par siège × sièges inclus.
  // Pas d'add-on pour l'instant (option future : ajouter `extra_mailbox` à QuantityAddonCode).
  [LimitResource.MAILBOX]: {
    resource: LimitResource.MAILBOX,
    getIncluded: (plan: Subcription, size: PlanSize): number =>
      getIncludedProEmailPerUser(plan) * getIncludedTeamSeats(plan, size === 'team'),
    upgrade_target_plan: 'max',
    error_key: 'MAILBOX_LIMIT_REACHED',
  },
};

export function getLimitDefinition(resource: LimitResource): LimitDefinition {
  return LIMITS_REGISTRY[resource];
}

/**
 * True si l'addon associé à la limite est activable sur le couple plan/size actuel.
 * Toujours false si la limite n'a pas d'addon.
 */
export function canActivateLimitAddon(resource: LimitResource, plan: Subcription, size: PlanSize): boolean {
  const def = LIMITS_REGISTRY[resource];
  if (!def.addon_code) return false;
  const plan_id = PLAN_ID_BY_ENUM[plan];
  return ADDONS[def.addon_code].is_eligible({ plan_id, size });
}

// =============================================================================
// Schéma de l'usage courant pour chaque ressource — exposé dans ProInfos.
// =============================================================================

export const ResourceUsageSchema = Type.Object({
  used: Type.Integer(),
  included: Type.Integer(),
  addon_extra: Type.Integer(),
  limit: Type.Integer(),
});
export type ResourceUsageObject = Static<typeof ResourceUsageSchema>;

export const SubscriptionUsageSchema = Type.Object({
  plan: Type.Enum(Subcription),
  plan_size: Type.Union([Type.Literal('solo'), Type.Literal('team')]),
  team_member: ResourceUsageSchema,
  gmb: ResourceUsageSchema,
});
export type SubscriptionUsageObject = Static<typeof SubscriptionUsageSchema>;
